import { DocumentType, WorkOrderRole } from "../types/Types";

// Mirrors server/utils/rnWorkOrderRoles.js: the RN screen must decide by the
// configured document type's workOrderRole, never by a literal code. Codes are only
// labels an admin may rename (6421 -> 6430), so stored RN entries also carry the
// `documentType` id and every lookup below prefers that id over the stored code.
// Literal codes survive only as the DEFAULT a historical entry falls back to when it
// predates both documentType and docTypeCode (see issueEntryRole/receiptEntryRole) and
// as the fixed razmer/prost server route suffixes (issue-6421-razmer, receive-7200-prost).

// An entry/config reference to a document type: id (string or populated) plus the code
// that was current when the entry was saved.
export interface DocTypeRef {
  documentType?: string | { _id?: string } | null;
  docTypeCode?: string | null;
}

const idOf = (value: string | { _id?: string } | null | undefined): string =>
  typeof value === "object" && value ? value._id || "" : value || "";

export function typeOfEntry(
  entry: DocTypeRef | null | undefined,
  documentTypes: DocumentType[]
): DocumentType | undefined {
  const id = idOf(entry?.documentType);
  return id ? documentTypes.find((type) => type._id === id) : undefined;
}

export function roleOfCode(
  code: string | null | undefined,
  documentTypes: DocumentType[]
): WorkOrderRole | null {
  if (!code) return null;
  return documentTypes.find((type) => type.code === code)?.workOrderRole ?? null;
}

// CURRENT code of the entry's document type: by id when the entry has one (snapshot
// codes go stale after a rename), otherwise the stored code of an older record.
export function currentDocTypeCode(
  entry: DocTypeRef | null | undefined,
  documentTypes: DocumentType[]
): string {
  return typeOfEntry(entry, documentTypes)?.code || entry?.docTypeCode || "";
}

// Role of a button-config / entry reference: id first, then its stored code.
export function roleOfRef(
  entry: DocTypeRef,
  documentTypes: DocumentType[]
): WorkOrderRole | null {
  const byId = typeOfEntry(entry, documentTypes);
  if (byId) return byId.workOrderRole ?? null;
  return roleOfCode(entry.docTypeCode, documentTypes);
}

// Does the entry point at the document type that currently has this code?
export function entryHasCode(
  entry: DocTypeRef | null | undefined,
  code: string,
  documentTypes: DocumentType[]
): boolean {
  return !!code && currentDocTypeCode(entry, documentTypes) === code;
}

// Historical RN entries saved before documentType/docTypeCode existed carry neither. An
// issue entry with neither defaults to '6421' (issue-materials) — the same default
// server/utils/rnWorkOrderRoles.js's issueRole() applies via loadRNRoleMap.
export function issueEntryRole(
  entry: DocTypeRef & { issueRole?: WorkOrderRole | "" | null },
  documentTypes: DocumentType[]
): WorkOrderRole | null {
  if (entry.issueRole) return entry.issueRole;
  if (typeOfEntry(entry, documentTypes)) return roleOfRef(entry, documentTypes);
  return roleOfCode(entry.docTypeCode || "6421", documentTypes);
}

// Same convention as issueEntryRole, but an entry with neither id nor code defaults to
// '7201' (receive-intermediate) — mirrors receiptRole() server-side.
export function receiptEntryRole(
  entry: DocTypeRef & { receiptRole?: WorkOrderRole | "" | null },
  documentTypes: DocumentType[]
): WorkOrderRole | null {
  if (entry.receiptRole) return entry.receiptRole;
  if (typeOfEntry(entry, documentTypes)) return roleOfRef(entry, documentTypes);
  return roleOfCode(entry.docTypeCode || "7201", documentTypes);
}

const ROLE_SUBCATEGORY: Partial<Record<WorkOrderRole, "issuance" | "reception">> = {
  "issue-materials": "issuance",
  "issue-intermediate": "issuance",
  "waste": "issuance",
  "sampling": "issuance",
  "receive-intermediate": "reception",
  "receive-finished": "reception",
};

// Current code of the document type that carries `role`: the RN's own button config
// first (resolved by id), else the single material type with that role and direction.
// Returns null when none or ambiguous — callers toast instead of guessing a literal.
// Intentional: a very old RN without `buttonConfig` plus two types sharing the same role
// lands here as null and the caller shows a message instead of guessing. No such RNs
// exist in demo/prod.
export function resolveRoleCode(
  role: WorkOrderRole,
  documentTypes: DocumentType[],
  buttonConfig?: DocTypeRef[] | null
): string | null {
  const configured = (buttonConfig || [])
    .filter((entry) => roleOfRef(entry, documentTypes) === role)
    .map((entry) => currentDocTypeCode(entry, documentTypes))
    .filter(Boolean);
  const unique = Array.from(new Set(configured));
  if (unique.length === 1) return unique[0];
  if (unique.length > 1) return null;
  const subCategory = ROLE_SUBCATEGORY[role];
  const candidates = documentTypes.filter((type) =>
    type.workOrderRole === role && type.category === "material"
    && (!subCategory || type.subCategory === subCategory)
  );
  return candidates.length === 1 ? candidates[0].code : null;
}
